import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout, { BreadcrumbItem } from '../../components/MainLayout';
import MisMateriasPage from './MisMateriasPage';
import MateriaRecursosPage from './MateriaRecursosPage';
import IngresarCodigoDialog from './IngresarCodigoDialog';
import { IMiMateria, IMiRecurso } from '../../types/miRecurso';
import { EResource } from '../../types/departamento';
import { RootState } from '../../state-management/store';
import { apiErrorMessage } from '../../api/catalog';
import { listStudentCourses, redeemCode, StudentCourseResponse } from '../../api/enrollments';
import { listStudentResources, resourceDownloadUrl } from '../../api/resources';
import {
	setMaterias,
	setSelectedMateriaId,
	setIngresarCodigoDialogOpen,
	resetMisMateriasPage,
} from '../../state-management/slices/MisMateriasPage';

const toMateria = (course: StudentCourseResponse): IMiMateria => ({
	id: course.id,
	nombre: course.subjectName,
	docenteNombre: course.teacherName,
	codigo: course.code,
	resourceCount: course.resourceCount,
	enrolledAt: new Date(course.enrolledAt),
});

const MisMateriasContainer: React.FC = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const [pageError, setPageError] = useState<string | null>(null);
	const [recursos, setRecursos] = useState<IMiRecurso[]>([]);
	const [selectedFileType, setSelectedFileType] = useState<EResource | 'all'>('all');
	const [searchQuery, setSearchQuery] = useState('');

	const { materias, selectedMateriaId, ingresarCodigoDialogOpen } = useSelector(
		(state: RootState) => state.misMateriasPage
	);

	useEffect(() => {
		let cancelled = false;
		dispatch(resetMisMateriasPage());
		setIsLoading(true);
		setPageError(null);
		listStudentCourses()
			.then((courses) => {
				if (!cancelled) dispatch(setMaterias(courses.map(toMateria)));
			})
			.catch((error: unknown) => {
				if (!cancelled) setPageError(apiErrorMessage(error));
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [location.pathname, dispatch]);

	useEffect(() => {
		if (selectedMateriaId === null) {
			setRecursos([]);
			return;
		}
		let cancelled = false;
		listStudentResources(selectedMateriaId)
			.then((rows) => {
				if (!cancelled) setRecursos(rows);
			})
			.catch((error: unknown) => {
				if (!cancelled) setPageError(apiErrorMessage(error));
			});
		return () => {
			cancelled = true;
		};
	}, [selectedMateriaId]);

	const selectedMateria = useMemo(
		() => materias.find((materia) => materia.id === selectedMateriaId) ?? null,
		[materias, selectedMateriaId]
	);

	const filteredRecursos = useMemo(() => {
		let result = recursos;
		if (selectedFileType !== 'all') {
			result = result.filter((recurso) => recurso.resourceType === selectedFileType);
		}
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			result = result.filter((recurso) => recurso.name.toLowerCase().includes(query));
		}
		return result;
	}, [recursos, selectedFileType, searchQuery]);

	const handleSelectMateria = (materia: IMiMateria) => {
		setSelectedFileType('all');
		setSearchQuery('');
		dispatch(setSelectedMateriaId(materia.id));
	};

	const handleBackToHub = () => {
		dispatch(setSelectedMateriaId(null));
		setSelectedFileType('all');
		setSearchQuery('');
	};

	const handleConfirmarCodigo = async (code: string) => {
		const course = await redeemCode(code);
		const courses = await listStudentCourses();
		dispatch(setMaterias(courses.map(toMateria)));
		dispatch(setIngresarCodigoDialogOpen(false));
		dispatch(setSelectedMateriaId(course.id));
	};

	const breadcrumbs: BreadcrumbItem[] = (() => {
		const crumbs: BreadcrumbItem[] = [{ label: 'Mis materias', onClick: handleBackToHub }];
		if (selectedMateria) crumbs.push({ label: selectedMateria.nombre });
		return crumbs;
	})();

	const { pageTitle, pageSubtitle } = selectedMateria
		? {
				pageTitle: selectedMateria.nombre,
				pageSubtitle: `Recursos de ${selectedMateria.docenteNombre}`,
			}
		: {
				pageTitle: 'Mis materias',
				pageSubtitle: 'Accede a los recursos de tus materias registradas',
			};

	return (
		<MainLayout pageTitle={pageTitle} pageSubtitle={pageSubtitle} breadcrumbs={breadcrumbs}>
			{pageError && (
				<Alert severity='error' onClose={() => setPageError(null)}>
					{pageError}
				</Alert>
			)}
			{selectedMateria ? (
				<MateriaRecursosPage
					materia={selectedMateria}
					recursos={filteredRecursos}
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					selectedFileType={selectedFileType}
					onFileTypeChange={setSelectedFileType}
					onDownload={(recurso) => window.open(resourceDownloadUrl(recurso.id), '_blank', 'noopener')}
				/>
			) : (
				<MisMateriasPage
					materias={materias}
					isLoading={isLoading}
					onSelectMateria={handleSelectMateria}
					onIngresarCodigo={() => dispatch(setIngresarCodigoDialogOpen(true))}
				/>
			)}

			<IngresarCodigoDialog
				open={ingresarCodigoDialogOpen}
				onClose={() => dispatch(setIngresarCodigoDialogOpen(false))}
				onConfirmar={handleConfirmarCodigo}
			/>
		</MainLayout>
	);
};

export default MisMateriasContainer;
